import { CYODesign } from "@shared/CYODesign.js";
import { Edge } from "@shared/BorderLoc.js";
import * as env from "@shared/environment.js";
import { dlog, dLogTime, dLogTimeEnd } from "@shared/utility.js";
import { getBackgroundRemovalService } from "@shared/configFeed.js";

const debugLayerExports = false;

// =================================
// Math Functions
// =================================

function linearRandom(min, max, randomState = null) {
    const random = randomState ? randomState.random() : Math.random();
    const result = random * (max - min) + min;
    
    
    return result;
}

// Calculate the length from the last point to the new one
function distance(x1,y1,x2,y2) {
    const dx = x2-x1;
    const dy = y2-y1;
    return Math.sqrt(dx*dx + dy*dy);
}

// =================================
// Canvas Functions
// =================================

// Draw the given img into the given canvas, with optional scaling.
function fitImageToCanvas(img, canvas, scale0=1) {
    const ctx = canvas.getContext("2d");
    var scale = scale0;
    if (img.height/img.width > canvas.height/canvas.width) {
        // background is taller than it is wide (relative to the canvas)... scale background image by width to match canvas width
        scale *= canvas.width / img.width;
    } else {
        // scale background image by height to match product canvas
        scale *= canvas.height / img.height;
    }

    // center background about canvas
    const x = (canvas.width - img.width*scale) / 2 ;
    const y = (canvas.height - img.height*scale) / 2;

    // draw images, scaled
    ctx.drawImage(img, x, y, img.width*scale, img.height*scale);
}

function rotatedCanvas(img, rotationAngle) {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // dlog(`img w/h: ${img.width}x${img.height}; angle: ${rotationAngle}`);
    
    const [rotW, rotH] = rotatedRectSize(img.width, img.height, rotationAngle);
    canvas.width = rotW;
    canvas.height = rotH;

    // dlog(`rotated w/h: ${rotW}/${rotH}`);

    // Move the context to the center of the image
    ctx.translate(rotW / 2, rotH / 2);

    // Rotate the context
    ctx.rotate(rotationAngle);

    // Draw the image, adjusting for the center
    ctx.drawImage(img, -img.width / 2, -img.height / 2);

    ctx.setTransform(1, 0, 0, 1, 0, 0); // reset

    return canvas;
}

function rotatedRectSize(inWidth, inHeight, inRotAngle) {
    const cos = Math.abs(Math.cos(inRotAngle));
    const sin = Math.abs(Math.sin(inRotAngle));
    
    const w = inWidth * cos + inHeight * sin;
    const h = inWidth * sin + inHeight * cos;
    
    return [w, h];
}

/**
 * Masks a canvas, making pixels outside the given rectangle transparent.
 *
 * @param {HTMLCanvasElement} inCanvas - The input canvas to be masked.
 * @param {number} x - The x-coordinate of the top-left corner of the rectangle.
 * @param {number} y - The y-coordinate of the top-left corner of the rectangle.
 * @param {number} w - The width of the rectangle.
 * @param {number} h - The height of the rectangle.
 * @returns {HTMLCanvasElement} A new canvas with the masked content.
 */
function maskCanvas(inCanvas, x, y, w, h) {
    const newCanvas = document.createElement("canvas");
    newCanvas.width = inCanvas.width;
    newCanvas.height = inCanvas.height;
    const ctx = newCanvas.getContext("2d");

    ctx.clearRect(0, 0, newCanvas.width, newCanvas.height);

    ctx.save();

    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();

    ctx.drawImage(inCanvas, 0, 0);

    ctx.restore();

    return newCanvas;
}

/**
 * Returns an independent copy of the given canvas
 *
 * @param {number} inset - The amount to inset each edge of the returned canvas.
 * @returns {HTMLCanvasElement} The new canvas.
 */
function copyCanvas(inCanvas, inset=0) {
    const copiedCanvas = document.createElement("canvas");
    const copiedCtx = copiedCanvas.getContext("2d");
    
    copiedCanvas.width = inCanvas.width - inset * 2;
    copiedCanvas.height = inCanvas.height - inset * 2;
    
    copiedCtx.drawImage(inCanvas, 0, 0);

    return copiedCanvas;
}

// Apply a mask given src file to the html canvas
async function layerImage(layerSrc, canvas, composite_op="destination-out") {
    const ctx = canvas.getContext("2d");
    return new Promise((resolve) => {
        const layer = new Image();
        layer.src = layerSrc;
        layer.onload = () => {
            ctx.globalCompositeOperation = composite_op;
            ctx.drawImage(layer, 0, 0, canvas.width, canvas.height);
            resolve();
        };
    });
}

// =================================
// Image Functions
// =================================

async function resizeImage(img, maxDim) {
    const widthRatio = maxDim / img.width;
    const heightRatio = maxDim / img.height;
    const ratio = Math.min(widthRatio, heightRatio, 1); // Ensure we never scale up

    // Bail if no resizing is needed
    if (ratio === 1) {
        return img;
    }

    // Determine new dimensions
    const newWidth = Math.floor(img.width * ratio);
    const newHeight = Math.floor(img.height * ratio);

    // Create a canvas to perform the resize operation
    const canvas = document.createElement("canvas");
    canvas.width = newWidth;
    canvas.height = newHeight;
    const ctx = canvas.getContext("2d");

    // Draw the resized image on the canvas
    ctx.drawImage(img, 0, 0, newWidth, newHeight);

    // Use a promise to handle the image conversion from canvas
    return new Promise((resolve, reject) => {
        const resizedImg = new Image();
        resizedImg.onload = () => resolve(resizedImg);
        resizedImg.onerror = reject;
        resizedImg.src = canvas.toDataURL();
    });
}

// Returns a canvas element resizing down for the given maxHeight, if necessary.
// NB: even if no resizing is necessary, this still creates and returns a canvas object.
function resizeImageOrCanvasForHeight(element, maxHeight) {
    dLogTime("resizeImageOrCanvasForHeight");
    
    const originalWidth = element.width;
    const originalHeight = element.height;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    var newWidth, newHeight;
    
    if (originalHeight <= maxHeight) {
        // If the height is less than or equal to maxHeight, return a canvas of the same size
        // Arguably for performance reasons this could return the img element as is, but then the caller has to worry about what they're getting back.
        newWidth = element.width;
        newHeight = element.height;
    } else {
        // Calculate the new dimensions while maintaining the aspect ratio
        const aspectRatio = originalWidth / originalHeight;
        newHeight = maxHeight;
        newWidth = newHeight * aspectRatio;   
    }

    // Create a canvas element to perform the resizing
    canvas.width = newWidth;
    canvas.height = newHeight;

    // Draw the image or canvas onto the canvas with the new dimensions
    ctx.drawImage(element, 0, 0, newWidth, newHeight);
    
    dLogTimeEnd("resizeImageOrCanvasForHeight");

    return canvas;    
}

async function convertImageToFile(img) {
    var canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;

    var ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg"));

    const file = new File([blob], "filename.jpg", { type: "image/jpeg", lastModified: Date.now() });

    return file;
}

function convertImageFormat(img, outputImgType, quality = 1) {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = img.width;
        canvas.height = img.height;

        ctx.drawImage(img, 0, 0);

        const currentImgType = img.src.split(";")[0].split("/")[1];
        if (currentImgType === outputImgType) {
            resolve(img);
            return;
        }

        const dataURL = canvas.toDataURL(`image/${outputImgType}`, quality);

        const outputImg = new Image();
        outputImg.onload = () => resolve(outputImg);
        outputImg.onerror = (e) => reject(e);

        outputImg.src = dataURL;
    });
}

function convertImageToBase64(img, outputImgType, quality = 1) {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = img.width;
        canvas.height = img.height;

        ctx.drawImage(img, 0, 0);

        const dataURL = canvas.toDataURL(`image/${outputImgType}`, quality);

        resolve(dataURL);
    });
}

// Determine which edges (bottom, left, and right, but currently not top) of the image are flat. Returns an [Edge].
function getFlatEdges(img) {
    dLogTime("getFlatEdges");

    var flatEdges = [];

    // Check for zero dimensions
    if (img.width === 0 || img.height === 0) {
        dLogTimeEnd("getFlatEdges");
        return flatEdges;
    }

    // draw image on a canvas
    var canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    var ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    // helper function to determine flatness for a given row/col of pixels
    function isFlat(data, totalLength, edge) {
        let flatCount = 0;
        let maxFlatCount = 0;
        // Was 0.2 but Altan found it then misses test image `cristofer-maximilian-unsplash` which only has 10.5% opaque pixels at the bottom, but definitely seems to be a "flat bottom" picture.
        const thresholdRatio = 0.10;
        
        // iterate through alpha channel values (every 4th value starting at index 3)
        for (let i = 3; i < data.length; i += 4) {
            if (data[i] < 255) {
                // Found a pixel with transparency
                flatCount = 0;
            } else {
                flatCount++;
                if (flatCount > maxFlatCount) {
                    maxFlatCount = flatCount;
                }
            }
        }
    
        const flatRatio = maxFlatCount / totalLength;
        dlog(`Percent of ${edge} that's non-transparent = ${(flatRatio * 100).toFixed(2)}%`);
        
        return flatRatio > thresholdRatio;
    }

    // check top edge
    const topY = 0;
    const topImageData = ctx.getImageData(0, topY, canvas.width, 1);
    const topData = topImageData.data;
    if (isFlat(topData, img.width, Edge.top)) {
        flatEdges.push(Edge.top);
    }

    // check bottom edge
    const bottomY = canvas.height - 1; // Last row
    const bottomImageData = ctx.getImageData(0, bottomY, canvas.width, 1);
    const bottomData = bottomImageData.data;
    if (isFlat(bottomData, img.width, Edge.bottom)) {
        flatEdges.push(Edge.bottom);
    }
    
    // check left edge
    const leftX = 0; // First column
    const leftImageData = ctx.getImageData(leftX, 0, 1, canvas.height);
    const leftData = leftImageData.data;
    if (isFlat(leftData, img.height, Edge.left)) {
        flatEdges.push(Edge.left);
    }
    
    // check right edge
    const rightX = canvas.width - 1; // Last column
    const rightImageData = ctx.getImageData(rightX, 0, 1, canvas.height);
    const rightData = rightImageData.data;
    if (isFlat(rightData, img.height, Edge.right)) {
        flatEdges.push(Edge.right);
    }
    
    dLogTimeEnd("getFlatEdges");

    return flatEdges;
}

// Like cropTransparentImageOld but MUCH (~10-20x) faster and returns a HTMLCanvasElement. (avg 25ms)
function cropTransparentImage(img, includeCropBoundsInResponse=false) {
    // dLogTime("cropTransparentImage");

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Draw the image onto the canvas
    canvas.width = img.width;
    canvas.height = img.height;        
    ctx.drawImage(img, 0, 0);

    // Get the image data
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const width = canvas.width;
    const height = canvas.height;

    // Threshold for considering a pixel "transparent" - ignore nearly-invisible pixels
    // that may be artifacts from background removal (e.g., alpha=2, Photoroom is doing this!)
    const alphaThreshold = 10;

    // Helper functions to check if a row or column is effectively transparent
    function isRowTransparent(y) {
        const startIndex = y * width * 4;
        for (let i = startIndex; i < startIndex + width * 4; i += 4) {
            if (data[i + 3] > alphaThreshold) {
                return false;
            }
        }
        return true;
    }

    function isColumnTransparent(x) {
        for (let y = 0; y < height; y++) {
            const index = (y * width + x) * 4;
            if (data[index + 3] > alphaThreshold) {
                return false;
            }
        }
        return true;
    }

    // Find the top boundary
    let minY = 0;
    while (minY < height && isRowTransparent(minY)) {
        minY++;
    }

    // Find the bottom boundary
    let maxY = height - 1;
    while (maxY > minY && isRowTransparent(maxY)) {
        maxY--;
    }

    // Find the left boundary
    let minX = 0;
    while (minX < width && isColumnTransparent(minX)) {
        minX++;
    }

    // Find the right boundary
    let maxX = width - 1;
    while (maxX > minX && isColumnTransparent(maxX)) {
        maxX--;
    }

    // Calculate the width and height of the cropped image
    const croppedWidth = maxX - minX + 1;
    const croppedHeight = maxY - minY + 1;

    // Create a new canvas for the cropped image
    const croppedCanvas = document.createElement("canvas");
    const croppedCtx = croppedCanvas.getContext("2d");
    croppedCanvas.width = croppedWidth;
    croppedCanvas.height = croppedHeight;

    // Draw the cropped image onto the new canvas
    croppedCtx.drawImage(
        canvas,
        minX,
        minY,
        croppedWidth,
        croppedHeight,
        0,
        0,
        croppedWidth,
        croppedHeight
    );

    // dLogTimeEnd("cropTransparentImage");

    if (includeCropBoundsInResponse) {
        return [croppedCanvas, [minX, minY, croppedWidth, croppedHeight]];
    } else {
        return croppedCanvas;
    }
}

// Assuming the incoming img has some transparency, cropTransparentImage removes rows and columns of pixels that are fully (mostly) transparent, and returns the cropped HTMLImageElement.
async function cropTransparentImageOld(img) {
    dLogTime("cropTransparentImageOld");

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

     // Draw the image onto the canvas
    canvas.width = img.width;
    canvas.height = img.height;
    
    ctx.drawImage(img, 0, 0);

    // Get the image data
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Find the bounding box of the non-transparent pixels
    let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
    for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
            const index = (y * canvas.width + x) * 4;
            const alpha = data[index + 3];
            if (alpha > 0) { // If pixel is not transparent
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }
    }

    // Calculate the width and height of the cropped image
    const width = maxX - minX + 1;
    const height = maxY - minY + 1;

    // dlog(`Original size: (${canvas.width},${canvas.height}), new: (${width},${height})`)
    // Create a new canvas for the cropped image
    const croppedCanvas = document.createElement("canvas");
    const croppedCtx = croppedCanvas.getContext("2d");
    croppedCanvas.width = width;
    croppedCanvas.height = height;

    // Draw the cropped image onto the new canvas
    croppedCtx.drawImage(canvas, minX, minY, width, height, 0, 0, width, height);

     // Convert the cropped canvas to a data URL
    const croppedImageURL = croppedCanvas.toDataURL();

    // Create an img element and set its src to the data URL
    const croppedImage = await loadImage(croppedImageURL);

    dLogTimeEnd("cropTransparentImageOld");

    return croppedImage;    
}

// Check if image is transparent through canvas image data
function isTransparent(img) {
    dLogTime("isTransparent"); // 8ms
    var canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    var ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    // Get the image data
    var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    var data = imageData.data;

    // Check if any pixel has an alpha value less than 255
    for (var i = 3; i < data.length; i += 4) {
        if (data[i] < 255) {
            // Found a pixel with transparency
            dlog("The image has a transparent layer");
            dLogTimeEnd("isTransparent");
            return true;
        }
    }
    dLogTimeEnd("isTransparent");
    return false;
}

function isFullyTransparent(img) {
    if (img.width == 0 && img.height == 0) {
        return true;
    }
    const [croppedImg, bounds] = cropTransparentImage(img, true);
    return bounds[2] == 0 && bounds[3] == 0;
}

async function loadFirebaseImage(src) {
    try {
        const response = await fetch(src);
        if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.statusText}`);
        }
        const blob = await response.blob();
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = (e) => reject(new Error(`Image load error: ${e}`));
            img.src = URL.createObjectURL(blob);
        });
    } catch (error) {
        console.error("Error in loadFirebaseImage:", error);
        throw error;
    }
}

async function loadImage(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.src = src;
    });
}

async function loadImageFromCanvas(canvas) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = canvas.toDataURL();
    });
}

function maskImageWithCircle(image, radius) {
    var canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;

    var ctx = canvas.getContext("2d");

    ctx.beginPath();

    // Start from the top-left corner
    ctx.moveTo(0, 0);

    // Draw line to the top-right corner
    ctx.lineTo(image.width, 0);

    // Draw line down to where the arc starts on the right edge
    ctx.lineTo(image.width, image.height - radius);

    // Draw the arc (bottom half of the circle) at the bottom of the image
    ctx.arc(
        image.width / 2,           // x-coordinate of the center
        image.height - radius,     // y-coordinate of the center
        radius,                    // radius of the circle
        0,                         // start angle (0 radians)
        Math.PI,                   // end angle (π radians)
        false                      // clockwise direction
    );

    // Draw line up to where the arc starts on the left edge
    ctx.lineTo(0, image.height - radius);

    // Close the path back to the starting point
    ctx.closePath();

    // Set the clipping region to the path we've just defined
    ctx.clip();

    // Draw the image; it will only appear within the clipping region
    ctx.drawImage(image, 0, 0);

    return canvas;
}

function outlineSizeFor(canvasWidth, canvasHeight) {
    var sz = (canvasWidth + canvasHeight) / 2 / 22;
    // "Try 45% of what it was"
    sz = sz * 0.45;
    return Math.max(sz, 6);
}

// =================================
// Debugging
// =================================

// For debugging, add the canvas to the end of the HTML and make it an <img> so that it's draggable
function debugAddCanvasToHTMLBody(canvas, label, useDebugBgColor=true) {
    // make a snapshot of given canvas so later changes don't affect its appearance
    const canvasCopy = copyCanvas(canvas);
    const ctx = canvasCopy.getContext("2d");
    
    if (useDebugBgColor) {
        ctx.fillStyle = "#59c4f6";
        ctx.globalCompositeOperation = "destination-atop";
        ctx.fillRect(0, 0, canvasCopy.width, canvasCopy.height);
    }

    const img = new Image();
    img.src = canvasCopy.toDataURL();
    if (label) {
        const p = document.createElement("p");
        p.innerHTML = label;
        document.body.appendChild(p);
    }
    document.body.appendChild(img);
}

// For debugging, add the given Image to the end of the HTML
function debugAddImageToHTMLBody(img, label, useDebugBgColor=true) {
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    fitImageToCanvas(img, canvas);
    debugAddCanvasToHTMLBody(canvas, label, useDebugBgColor);
}

// **************************
// CF Worker Image Processing
// **************************

async function cfProcessImage(file) {
    try {
        dLogTime("cfProcessImage");

        // Get a JWT token
        const getTokenResponse = await fetch(env.getJWTTokenURL(), {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        });

        if (!getTokenResponse.ok) {
            throw new Error("Failed to fetch auth token");
        }

        const data = await getTokenResponse.json();
        const token = data.token;
        if (!token.trim()) {
            throw new Error("Received an empty auth token");
        }

        // Validate API URL
        const url = env.removeImageBGURL();
        if (!url) {
            throw new Error("Invalid removeImageBGURL()");
        }
        
        // Prepare form data
        const formData = new FormData();
        formData.append("image_file", file);

        const bgRemovalService = getBackgroundRemovalService();
        if (bgRemovalService !== null) {
            formData.append("bgRemovalService", bgRemovalService);
        }

        const headers = {
            "Authorization": `Bearer ${token}`,
        };

        dlog(`POST ⬆️: ${url}`);

        // Send request to remove background
        const response = await fetch(url, {
            method: "POST",
            headers: headers,
            body: formData
        });

        if (!response.ok) {
            throw new Error(`Failed to remove background: ${response.status} ${response.statusText}`);
        }

        // Check for people or pets detection header
        const hasPeopleOrPetsHeader = response.headers.get("X-Has-People-Or-Pets");
        const hasPeopleOrPets = hasPeopleOrPetsHeader === "true";

        // Convert response to Blob and return URL
        const imageBlob = await response.blob();
        const src = URL.createObjectURL(imageBlob);

        dlog("⬇️ Received image with removed background from API");
        dlog(`People/pets detected: ${hasPeopleOrPets}`);

        dLogTimeEnd("cfProcessImage");        
        return { src, hasPeopleOrPets };

    } catch (error) {
        console.error("*** Fetch error:", error);
        dLogTimeEnd("cfProcessImage");
        return null;
    }
}

async function imageToBlob(imageElement) {
    if (imageElement instanceof HTMLCanvasElement) {
        return new Promise((resolve, reject) => {
            imageElement.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error("Failed to convert canvas to blob"));
                }
            }, "image/png");
        });
    } else if (imageElement instanceof HTMLImageElement) {
        const canvas = document.createElement("canvas");
        canvas.width = imageElement.naturalWidth || imageElement.width;
        canvas.height = imageElement.naturalHeight || imageElement.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(imageElement, 0, 0);

        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error("Failed to convert image to blob"));
                }
            }, "image/png");
        });
    } else {
        throw new Error("Unsupported image type");
    }
}

function resizeCanvas(sourceCanvas, maxSize) {
    const { width, height } = sourceCanvas;

    if (width <= maxSize && height <= maxSize) {
        const copy = document.createElement("canvas");
        copy.width = width;
        copy.height = height;
        const ctx = copy.getContext("2d");
        ctx.drawImage(sourceCanvas, 0, 0);
        return copy;
    }

    const scale = Math.min(maxSize / width, maxSize / height);
    const newWidth = Math.round(width * scale);
    const newHeight = Math.round(height * scale);

    const resized = document.createElement("canvas");
    resized.width = newWidth;
    resized.height = newHeight;
    const ctx = resized.getContext("2d");
    ctx.drawImage(sourceCanvas, 0, 0, newWidth, newHeight);
    return resized;
}

async function loadImageFromURL(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = url;
    });
}

export { linearRandom, distance, fitImageToCanvas, rotatedCanvas, maskCanvas, copyCanvas, layerImage, resizeImage, resizeImageOrCanvasForHeight, convertImageToFile, convertImageFormat, convertImageToBase64, getFlatEdges, isTransparent, isFullyTransparent, loadFirebaseImage, loadImage, loadImageFromCanvas, maskImageWithCircle, debugAddCanvasToHTMLBody, debugAddImageToHTMLBody, cfProcessImage, outlineSizeFor, cropTransparentImage, cropTransparentImageOld, imageToBlob, resizeCanvas, loadImageFromURL };